import { FC, useMemo } from "react";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import {
  FavoriteDocumentsDataSource,
  FavoriteDocumentsWidgetConfig,
} from "@/models/dashboard/WidgetConfig";
import useUsers from "@/hooks/useUsers";
import {
  buildFavoriteDocumentItems,
  buildFullDisplayOrderForGlobalUpdate,
  filterFavoriteDocuments,
  resolveItemOrder,
  usesWidgetItemOrder,
} from "../lib/resolveItemOrder";
import UserDataOrderSection from "./UserDataOrderSection";

interface FavoriteDocumentsConfigFormProps {
  config: FavoriteDocumentsWidgetConfig;
  onChange: (config: FavoriteDocumentsWidgetConfig) => void;
}

const FavoriteDocumentsConfigForm: FC<FavoriteDocumentsConfigFormProps> = ({
  config,
  onChange,
}) => {
  const { useQueryUsersFavoriteDocuments, reorderFavoriteDocuments } =
    useUsers();
  const favoritesQuery = useQueryUsersFavoriteDocuments();

  const dataSource = config.dataSource ?? "all";
  const useGlobalOrder = !usesWidgetItemOrder(
    config.useGlobalOrder,
    config.itemOrder
  );

  const displayItems = useMemo(() => {
    if (!favoritesQuery.data) return [];

    const allItems = buildFavoriteDocumentItems(
      favoritesQuery.data.favoriteLegislations,
      favoritesQuery.data.favoriteWorkspaceDocuments
    );
    const filtered = filterFavoriteDocuments(allItems, dataSource);
    const ordered = resolveItemOrder(filtered, {
      globalOrder: favoritesQuery.data.displayOrder,
      itemOrder: config.itemOrder,
      useGlobalOrder: config.useGlobalOrder,
    });

    return ordered.map((item) => ({
      id: item.id,
      label: item.document.title,
      sublabel: item.type === "legislation" ? "Kilder" : "Workspace",
    }));
  }, [favoritesQuery.data, dataSource, config.itemOrder, config.useGlobalOrder]);

  const currentOrderedIds = useMemo(() => {
    if (!useGlobalOrder && config.itemOrder && config.itemOrder.length > 0) {
      return config.itemOrder;
    }
    return displayItems.map((item) => item.id);
  }, [useGlobalOrder, config.itemOrder, displayItems]);

  const updateConfig = (updates: Partial<FavoriteDocumentsWidgetConfig>) => {
    onChange({ ...config, ...updates });
  };

  const handleDataSourceChange = (value: FavoriteDocumentsDataSource) => {
    updateConfig({ dataSource: value });
  };

  const handleUseGlobalOrderChange = (useGlobal: boolean) => {
    if (useGlobal) {
      updateConfig({ useGlobalOrder: true, itemOrder: undefined });
    } else {
      updateConfig({
        useGlobalOrder: false,
        itemOrder: currentOrderedIds,
      });
    }
  };

  const handleItemOrderChange = (orderedIds: string[]) => {
    updateConfig({ useGlobalOrder: false, itemOrder: orderedIds });
  };

  const handleUpdateGlobalOrder = async () => {
    if (!favoritesQuery.data) return;

    const allItems = buildFavoriteDocumentItems(
      favoritesQuery.data.favoriteLegislations,
      favoritesQuery.data.favoriteWorkspaceDocuments
    );
    const allIds = allItems.map((item) => item.id);
    const ids = buildFullDisplayOrderForGlobalUpdate(
      currentOrderedIds,
      allIds,
      favoritesQuery.data.displayOrder
    );
    await reorderFavoriteDocuments.mutateAsync(ids);
    updateConfig({ useGlobalOrder: true, itemOrder: undefined });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Vis dokumenter</Label>
        <Select
          value={dataSource}
          onValueChange={(value) =>
            handleDataSourceChange(value as FavoriteDocumentsDataSource)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle favoritter</SelectItem>
            <SelectItem value="sources">Kun kilder (lovgivning)</SelectItem>
            <SelectItem value="workspace-documents">
              Kun workspace-dokumenter
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <UserDataOrderSection
        useGlobalOrder={useGlobalOrder}
        items={displayItems}
        droppableId="favorite-documents-config-order"
        onUseGlobalOrderChange={handleUseGlobalOrderChange}
        onItemOrderChange={handleItemOrderChange}
        onUpdateGlobalOrder={handleUpdateGlobalOrder}
        isUpdatingGlobal={reorderFavoriteDocuments.isPending}
      />
    </div>
  );
};

export default FavoriteDocumentsConfigForm;
